import React from 'react';
import PropTypes from 'prop-types';
import { Line, Bar } from 'react-chartjs-2';
import {
    chartConfig,
    chartRange,
    ChartToolbar,
    ChartError,
    chartConfigurator,
    defaultChartColours
} from "../../utilities/ChartConfig";
import 'chartjs-plugin-annotation';
import 'chartjs-plugin-style';
import { useAppContext } from '../../contexts/MarketPerformanceContext';
import { LottieLoader } from "../../utilities/Loaders";
import { truncateNumber } from "../../utilities/Utils";

const PriceChart = () => {
    // Hoist
    const context = useAppContext();

    if (context.state.showError) {
        return <ChartError />
    }

    if (context.state.instrument.loading) {
        return loadingGraphBlock();
    }

    const values = context.state.instrument.values ? context.state.instrument.values : [];

    if (hasValidValues(values, 'amount') === false) {
        return <ChartError message="No price data available for this date range" />
    }

    const graphCustomisation = context.state.instrument.graphCustomisation || false;
    const minY = graphCustomisation ? graphCustomisation.minYValue : 0;
    const maxY = graphCustomisation ? graphCustomisation.maxYValue : 1;

    const config = chartConfig(minY, maxY, values[0].currency);

    chartConfigurator.setup(config);
    chartConfigurator.sync();

    const timeConfig = chartRange(context.state.currentChartRangeId);
    chartConfigurator.mergeXAxisTimeScale(timeConfig);

    chartConfigurator.addValues(values, function (item) {
        return {
            x: new Date(item.asAt),
            y: item.amount,
        }
    });

    // Add annotations
    if (context.state.currentChartRangeId === 'T' && values.length) {
        const { openValue, previousCloseValue } = context.state.instrument ? context.state.instrument : [];

        if (previousCloseValue || openValue) {
            const { amount, currency } = previousCloseValue ? previousCloseValue : openValue;

            config.options.annotation = {
                annotations: [
                    {
                        drawTime: 'afterDatasetsDraw',
                        id: 'hline',
                        type: 'line',
                        mode: 'horizontal',
                        scaleID: 'y-axis-0',
                        value: amount,
                        borderColor: '#95999C',
                        borderWidth: 1,
                        borderDash: [8, 8],
                        label: {
                            content: `PREVIOUS CLOSE ${Number(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')} ${currency}`,
                            enabled: true,
                            backgroundColor: 'rgba(244, 244, 245, 0.7)',
                            fontColor: '#212D3B',
                            fontSize: 12,
                            fontWeight: 'bold',
                            position: 'left',
                            yAdjust: -10,
                            xAdjust: 5,
                            xPadding: 3,
                            yPadding: 3,
                        },
                    },
                ],
            };
        }
    }

    return <Line {...config} />
}

function VolumeChart()
{
    const context = useAppContext();

    if (context.state.showError) {
        return <ChartError />
    }

    if (context.state.instrument.loading) {
        return loadingGraphBlock();
    }

    const values = context.state.instrument.volumes ? context.state.instrument.volumes : [];

    // some response data returns with array of volumes but no volume property on each item in array
    if (hasValidValues(values, 'volume') === false) {
        return <ChartError message="No volume data available for this date range" />
    }

    const config = chartConfig();
    chartConfigurator.setup(config);

    chartConfigurator.addValues(values, function (item) {
        return {
            x: new Date(item.asAt),
            y: item.volume,
        }
    });

    const timeConfig = chartRange(context.state.currentChartRangeId);
    chartConfigurator.mergeXAxisTimeScale(timeConfig);

    chartConfigurator.sync();
    chartConfigurator.disablePointerLine();

    chartConfigurator.setDatasetBackgroundColours({
        backgroundColour: defaultChartColours.silverFernGrey,
        hoverBackgroundColour: defaultChartColours.highCliffGreen,
    });

    chartConfigurator.setTooltipLabel(function (item) {
        const { value, label } = item;
        const formatted = truncateNumber(value, 2, String(1000));

        return `Volume: ${formatted} (${label})`;
    });

    chartConfigurator.setYAxisLabel(function (label) {
        if (label == 0) {
            return 0;
        }

        return truncateNumber(label, 0, String(1000));
    });

    return <Bar {...config} />
}

function PerformanceChart(props) {
    const context = useAppContext();

    return (
        <div className={`chart ${props.name}`}>
            <ChartToolbar extraClasses="fb-mb-4" context={context} />
            <div className="chart-container">
                <PriceChart />
            </div>
            {props.showVolume && (
                <div className="chart-container mt-5">
                    <VolumeChart />
                </div>
            )}
        </div>
    );
}

function hasValidValues(values, requiredProperty) {
    if (values.length < 1) {
        return false;
    }

    const validValues = values.filter((item) => item[requiredProperty] > 0);

    return validValues.length > 0;
}

function loadingGraphBlock() {
    return (
        <div className="empty d-table text-center w-100">
            <div className="d-table-cell align-middle h5 font-weight-bold">
                <LottieLoader message={'Loading graph'} />
            </div>
        </div>
    );
}

PerformanceChart.propTypes = {
    name: PropTypes.string.isRequired,
    showVolume: PropTypes.bool,
};

export default PerformanceChart;
